import {
    getGqlDataFromResponse,
    getGqlRequestId,
    SharedCache,
    type ValidCacheData,
} from "@khanacademy/wonder-blocks-data";
import type {GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import {DefaultFastlyQueryScope} from "./constants.ts";
import {toGqlContextRecord} from "./to-gql-context-record.ts";
import {toNormalizedOperation} from "./to-normalized-operation.ts";
import {
    type GqlContext,
    type GqlFetchFn,
    type GqlFetchOptions,
} from "./types.ts";
import {wbdGqlFetch} from "./wbd-gql-fetch.ts";

/**
 * Perform a GQL operation outside of the React component tree.
 *
 * Use this *only* if you're unable to use the React hooks for performing
 * a gql operation. Try to pass reference to a React-ready approach by using
 * `useGql` in a relevant component and passing it through. This will ensure
 * that appropriate context is inherited and used.
 *
 * Using this method will not only mean that any context setup by the
 * React app is not going to be honored automatically, but also that any
 * mocking performed with the gql test harness adapter will not be applied.
 * Since this ultimately invokes `@khan/data-fetch` fetch function, mocking of
 * these calls must be done using the associated test harness adapter or
 * mocking API.
 *
 * Finally, note that this only uses local caching for fastly-cacheable queries.
 * All other calls will lead to a network request.
 */
export const standaloneGqlFetch: GqlFetchFn = async <
    TData extends ValidCacheData,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    options: GqlFetchOptions<TVariables> = Object.freeze({}),
): Promise<TData> => {
    const {variables, context: partialContext} = options;

    // Make sure we have a complete context.
    const context: GqlContext = {
        targetLocale: partialContext?.targetLocale ?? RequestInfo.kaLocale,
        // NOTE(somewhatabstract, FEI-4906): We do not default the
        // curriculum value here. This gives us a better cache hit
        // rates for operations that don't need it. Operations that
        // require the curriculum context value should explicitly
        // provide it.
        curriculum: partialContext?.curriculum,
        fastlyCaching: partialContext?.fastlyCaching,
    };

    // We need to convert things to Wonder Blocks types.
    const wbdOperation = toNormalizedOperation(operation);
    const wbdContext = toGqlContextRecord(context);

    // If we're in dev or test, let's do some checking to make sure the call
    // appears valid.
    if (process.env.NODE_ENV !== "production") {
        if (wbdOperation.type !== "query" && wbdContext.fastlyCaching != null) {
            throw new KAError(
                "Cannot use Fastly caching with mutations.",
                Errors.NotAllowed,
            );
        }
    }

    const isFastlyCacheable =
        wbdOperation.type === "query" && wbdContext.fastlyCaching != null;

    const requestId = getGqlRequestId(wbdOperation, variables, wbdContext);
    const cachedResult = isFastlyCacheable
        ? SharedCache.get(DefaultFastlyQueryScope, requestId)
        : null;

    if (cachedResult != null) {
        // We assert that the cached value is the same type as what we want
        // to return.
        return cachedResult as TData;
    }

    // Normally, Wonder Blocks Data is ultimately responsible for calling
    // the `wbdGqlFetch` function that we register with it and decoding the
    // response. However, by definition, it cannot since we our outside React,
    // so instead, we call it here.
    const response = await wbdGqlFetch(wbdOperation, variables, wbdContext);

    // Now that we have the response, let's get Wonder Blocks Data to decode it
    // so that our behavior matches what we do in the rest of our wbdGqlFetch's.
    const result = await getGqlDataFromResponse<TData>(response);

    if (isFastlyCacheable) {
        // We need to update the cache.
        SharedCache.set(DefaultFastlyQueryScope, requestId, result);
    }

    return result;
};
